import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const TYPE_LABEL = { select: "Choice", text: "Text", textarea: "Long text", number: "Number", date: "Date", boolean: "Yes / No", multiselect: "Multi-choice", file: "File" };

// ── STR template section display ──────────────────────────────────────────────
function StrTemplateDetail({ code }) {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/strategic-review/templates/${code}`)
      .then((r) => setTpl(r.data))
      .catch(() => setTpl(null))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="py-4 text-center"><Icons.Loader2 className="h-4 w-4 animate-spin text-indigo-500 inline" /></div>;
  if (!tpl) return <p className="text-xs text-slate-400 py-2">Could not load STR template.</p>;

  const sections = tpl.sections || [];
  const modules  = tpl.modules  || [];
  const qCount   = modules.reduce((s, m) => s + (m.question_codes?.length || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        {[
          { label: "Required modules",  value: (tpl.required_modules || []).length },
          { label: "Default modules",   value: (tpl.default_modules  || []).length },
          { label: "Optional modules",  value: (tpl.optional_modules || []).length },
          { label: "Total questions",   value: qCount },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-lg border border-slate-100 px-3 py-2 text-center">
            <div className="text-lg font-semibold text-slate-800">{value}</div>
            <div className="text-[11px] text-slate-400">{label}</div>
          </div>
        ))}
      </div>

      {sections.length > 0 ? sections.map((s) => (
        <div key={s.key || s.title}>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{s.title || s.key}</div>
          <div className="space-y-1">
            {(s.questions || []).map((q) => (
              <div key={q.code} className="flex items-center gap-2 text-sm text-slate-700 bg-white rounded-lg border border-slate-100 px-3 py-1.5">
                <span className="flex-1">{q.text}</span>
                {q.required && <span className="text-[10px] font-medium text-rose-500 uppercase">Required</span>}
                {q.ptf_mapping && <span className="text-[10px] text-indigo-500 uppercase" title={`Profile field: ${q.ptf_mapping}`}>Prefillable</span>}
                {q.show_if && <span className="text-[10px] font-medium text-amber-600 uppercase">Conditional</span>}
                <span className="text-[11px] text-slate-400">{q.response_type}</span>
              </div>
            ))}
          </div>
        </div>
      )) : modules.map((m) => (
        <div key={m.code}>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            {m.name || m.code}
            {m.module_type && <span className="ml-2 text-[10px] text-indigo-400 normal-case font-normal">{m.module_type}</span>}
          </div>
          <div className="space-y-1">
            {(m.question_codes || []).map((qc) => (
              <div key={qc} className="flex items-center gap-2 text-sm text-slate-700 bg-white rounded-lg border border-slate-100 px-3 py-1.5">
                <Icons.Circle className="h-2 w-2 text-slate-300 shrink-0" />
                <span className="flex-1 text-slate-500">{qc}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OrganizerTemplates() {
  const [list, setList] = useState(null);
  const [strList, setStrList] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/organizers/templates").then((r) => setList(r.data.templates || [])).catch(() => setList([]));
    api.get("/strategic-review/templates").then((r) => setStrList(r.data?.templates || r.data || [])).catch(() => setStrList([]));
  }, []);

  const toggle = async (key) => {
    if (open === key) { setOpen(null); return; }
    setOpen(key); setDetail(null); setLoading(true);
    try { const r = await api.get(`/organizers/templates/${key}`); setDetail(r.data); }
    catch { setDetail(null); } finally { setLoading(false); }
  };

  const isReady = list !== null && strList !== null;

  return (
    <div className="p-6 max-w-[1000px] mx-auto space-y-6" data-testid="organizer-templates">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Organizer Templates</h1>
        <p className="text-sm text-slate-500">The question libraries that power client organizers &amp; tax interviews. Read-only preview of every section and question.</p>
      </div>

      {!isReady ? (
        <div className="py-10 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>
      ) : (
        <>
          {/* Canonical intake organizers */}
          {list.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Icons.ClipboardList className="h-4.5 w-4.5 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Client Intake Organizers</h2>
              </div>
              <div className="space-y-2.5">
                {list.map((t) => (
                  <div key={t.key} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`org-template-${t.key}`}>
                    <button onClick={() => toggle(t.key)} className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 transition-colors" data-testid={`org-template-toggle-${t.key}`}>
                      <div className="flex items-center gap-3 text-left">
                        <span className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Icons.ClipboardList className="h-4.5 w-4.5" /></span>
                        <div>
                          <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                          <div className="text-xs text-slate-400">{t.sections} sections · {t.questions} questions · v{t.version}</div>
                        </div>
                      </div>
                      <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === t.key ? "rotate-180" : ""}`} />
                    </button>
                    {open === t.key && (
                      <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/50">
                        {loading ? <div className="py-4 text-center"><Icons.Loader2 className="h-4 w-4 animate-spin text-blue-500 inline" /></div> : !detail ? (
                          <p className="text-xs text-slate-400 py-2">Could not load template.</p>
                        ) : (
                          <div className="space-y-4">
                            {detail.sections.map((s) => (
                              <div key={s.key}>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{s.label}</div>
                                <div className="space-y-1">
                                  {s.questions.map((q) => (
                                    <div key={q.key} className="flex items-center gap-2 text-sm text-slate-700 bg-white rounded-lg border border-slate-100 px-3 py-1.5">
                                      <span className="flex-1">{q.label}</span>
                                      {q.required && <span className="text-[10px] font-medium text-red-500 uppercase">Required</span>}
                                      {q.conditional && <span className="text-[10px] font-medium text-amber-600 uppercase" title="Shown conditionally">Conditional</span>}
                                      <span className="text-[11px] text-slate-400">{TYPE_LABEL[q.type] || q.type}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STR Organizer templates */}
          {(strList || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Icons.Compass className="h-4.5 w-4.5 text-indigo-400" />
                <h2 className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">Strategic Tax Review Organizer</h2>
                <span className="text-[11px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">ADV-STRATEGY</span>
              </div>
              <div className="space-y-2.5">
                {strList.map((t) => {
                  const strKey = `str-${t.code}`;
                  const modCount = (t.required_modules?.length || 0) + (t.default_modules?.length || 0) + (t.optional_modules?.length || 0);
                  return (
                    <div key={t.code} className="bg-white rounded-xl border border-indigo-200 shadow-sm overflow-hidden" data-testid={`str-template-${t.code}`}>
                      <button onClick={() => setOpen(open === strKey ? null : strKey)}
                        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-indigo-50/40 transition-colors" data-testid={`str-template-toggle-${t.code}`}>
                        <div className="flex items-center gap-3 text-left">
                          <span className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Icons.Compass className="h-4.5 w-4.5" /></span>
                          <div>
                            <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                            <div className="text-xs text-slate-400">
                              {t.code} · v{t.version} · {modCount} modules · jurisdiction-aware assembly
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">STR Organizer</span>
                          <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === strKey ? "rotate-180" : ""}`} />
                        </div>
                      </button>
                      {open === strKey && (
                        <div className="border-t border-indigo-100 px-4 py-3 bg-indigo-50/20">
                          <StrTemplateDetail code={t.code} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-2">
                STR Organizer templates use dynamic module assembly — the questions shown to each client depend on their jurisdiction, Tax Profile, and prior answers.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
